import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Filter,
  TrendingDown,
  Users,
  CheckCircle,
  XCircle,
  ShoppingBag,
  ArrowRight,
  ChevronRight,
  X,
} from 'lucide-react';
import { apiClient, type FunnelOverview, type FunnelStageContacts } from '../lib/api.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import {
  cn,
  formatBRL,
  formatPercent,
  formatRelative,
  stageColor,
  stageLabel,
  temperatureColor,
  temperatureLabel,
} from '../lib/utils.js';

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function previousMonth(month: string): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 7);
}

function nextMonth(month: string): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + 1);
  return d.toISOString().slice(0, 7);
}

function FunnelBar({
  stage,
  count,
  maxCount,
  dropoffPct,
  isSelected,
  onClick,
}: {
  stage: string;
  count: number;
  maxCount: number;
  dropoffPct: number | null;
  isSelected: boolean;
  onClick: () => void;
}): JSX.Element {
  const widthPct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  const isPositiveDropoff = dropoffPct !== null && dropoffPct > 0;
  const isNegativeDropoff = dropoffPct !== null && dropoffPct < 0;
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left flex items-center gap-3 group',
        isSelected && 'ring-2 ring-primary rounded-md',
      )}
    >
      <div className="w-32 shrink-0 text-right text-xs">
        <div className="text-muted-foreground">{stageLabel[stage] ?? stage}</div>
        {dropoffPct !== null && (
          <div
            className={cn(
              'text-[10px]',
              isPositiveDropoff
                ? 'text-rose-400'
                : isNegativeDropoff
                ? 'text-emerald-400'
                : 'text-muted-foreground/60',
            )}
          >
            {isPositiveDropoff ? '↓' : isNegativeDropoff ? '↑' : '–'}{' '}
            {Math.abs(dropoffPct * 100).toFixed(0)}%
          </div>
        )}
      </div>
      <div className="flex-1 relative">
        <div
          className={cn(
            'h-10 rounded-md flex items-center px-3 transition-all',
            stageColor[stage] ?? 'bg-slate-500/15 text-slate-300',
            'group-hover:brightness-110',
          )}
          style={{ width: `${Math.max(widthPct, 8)}%` }}
        >
          <span className="text-sm font-semibold tabular-nums">{count}</span>
        </div>
      </div>
      <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
    </button>
  );
}

function StageContacts({ stage }: { stage: string }): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['funnel-stage', stage],
    queryFn: () => apiClient.funnelStageContacts(stage, 50),
  });

  if (isLoading) {
    return <div className="text-sm text-muted-foreground p-4">Carregando contatos...</div>;
  }
  if (!data || data.contacts.length === 0) {
    return (
      <div className="text-sm text-muted-foreground p-4 text-center">
        Nenhum contato em <strong>{stageLabel[stage] ?? stage}</strong>.
      </div>
    );
  }

  return (
    <div className="divide-y divide-border/40 max-h-[500px] overflow-y-auto">
      {data.contacts.map((c) => (
        <Link
          key={c.id}
          to={`/contatos/${c.id}`}
          className="block px-4 py-2.5 hover:bg-muted/30"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-sm font-medium truncate">
                  {c.displayName ?? '—'}
                </span>
                <Badge
                  variant="outline"
                  className={cn('text-[10px]', temperatureColor[c.temperature])}
                >
                  {temperatureLabel[c.temperature] ?? c.temperature}
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground truncate">{c.emailDisplay}</div>
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-0.5 flex-wrap">
                {(c.geoCity || c.geoState) && (
                  <span>{[c.geoCity, c.geoState].filter(Boolean).join(' / ')}</span>
                )}
                {c.utmSource && (
                  <Badge variant="outline" className="text-[10px]">
                    {c.utmSource}
                    {c.utmCampaign ? ` · ${c.utmCampaign}` : ''}
                  </Badge>
                )}
              </div>
            </div>
            <div className="text-right shrink-0">
              {c.ordersCount > 0 ? (
                <>
                  <div className="text-sm font-medium text-emerald-400 tabular-nums">
                    {formatBRL(c.ltvCents)}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {c.ordersCount} pedido{c.ordersCount === 1 ? '' : 's'}
                  </div>
                </>
              ) : (
                <div className="text-[10px] text-muted-foreground">sem compra</div>
              )}
              {c.lastSeenAt && (
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  visto {formatRelative(c.lastSeenAt)}
                </div>
              )}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

export function Funil(): JSX.Element {
  const [month, setMonth] = useState<string>(currentMonth());
  const [selectedStage, setSelectedStage] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['funnel-overview', month],
    queryFn: () => apiClient.funnelOverview(month),
  });

  if (isLoading) {
    return <div className="p-8 text-muted-foreground">Carregando funil...</div>;
  }
  if (isError || !data) {
    return <div className="p-8 text-rose-400">Erro: {String(error)}</div>;
  }

  const { funnel, dropouts, kpis } = data;
  const maxCount = Math.max(...funnel.map((f) => f.count), 1);
  const maxDropout = Math.max(...dropouts.map((d) => d.count), 1);

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Filter className="h-6 w-6 text-primary" />
            Funil de Conversão
          </h1>
          <p className="text-sm text-muted-foreground">
            PIX → venda → upsell · da operação real · dropoff entre etapas · clique para drill-down
          </p>
        </div>

        <div className="flex items-center gap-2 bg-muted/40 rounded-md px-1 py-1 border border-border">
          <button
            onClick={() => setMonth(previousMonth(month))}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
          >
            ←
          </button>
          <div className="px-3 py-1 text-sm font-medium tabular-nums">{month}</div>
          <button
            onClick={() => setMonth(nextMonth(month))}
            disabled={month >= currentMonth()}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-30"
          >
            →
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <Users className="h-3.5 w-3.5" /> Total no funil
            </div>
            <div className="text-2xl font-bold tabular-nums">{kpis.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <Users className="h-3.5 w-3.5" /> Top of funnel
            </div>
            <div className="text-2xl font-bold text-blue-400 tabular-nums">
              {kpis.topOfFunnel}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <ShoppingBag className="h-3.5 w-3.5" /> Customers
            </div>
            <div className="text-2xl font-bold text-emerald-400 tabular-nums">
              {kpis.customers}
            </div>
            <div className="text-[10px] text-muted-foreground">
              {formatPercent(kpis.checkoutToCustomerRate)} checkout→customer
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <CheckCircle className="h-3.5 w-3.5" /> Won
            </div>
            <div className="text-2xl font-bold text-emerald-400 tabular-nums">
              {kpis.wonCount}
            </div>
            <div className="text-[10px] text-muted-foreground">
              {formatBRL(kpis.revenueCents)} no mês
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <XCircle className="h-3.5 w-3.5" /> Lost
            </div>
            <div className="text-2xl font-bold text-rose-400 tabular-nums">
              {kpis.lostCount}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Funnel + Drilldown side-by-side */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Funnel chart */}
        <div className={selectedStage ? 'lg:col-span-2' : 'lg:col-span-3'}>
          <Card>
            <CardHeader>
              <CardTitle>Etapas do funil</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 py-4">
              {funnel.map((row) => (
                <FunnelBar
                  key={row.stage}
                  stage={row.stage}
                  count={row.count}
                  maxCount={maxCount}
                  dropoffPct={row.dropoffPct}
                  isSelected={selectedStage === row.stage}
                  onClick={() =>
                    setSelectedStage((s) => (s === row.stage ? null : row.stage))
                  }
                />
              ))}
            </CardContent>
          </Card>

          {/* Dropouts */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingDown className="h-4 w-4 text-rose-400" />
                Estados de saída
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 py-4">
              {dropouts.map((d) => (
                <button
                  key={d.stage}
                  onClick={() =>
                    setSelectedStage((s) => (s === d.stage ? null : d.stage))
                  }
                  className={cn(
                    'w-full text-left flex items-center gap-3 group',
                    selectedStage === d.stage && 'ring-2 ring-primary rounded-md',
                  )}
                >
                  <div className="w-32 shrink-0 text-right text-xs text-muted-foreground">
                    {stageLabel[d.stage] ?? d.stage}
                  </div>
                  <div className="flex-1 relative">
                    <div
                      className={cn(
                        'h-8 rounded-md flex items-center px-3 transition-all group-hover:brightness-110',
                        d.stage === 'lost'
                          ? 'bg-rose-500/20 text-rose-300'
                          : 'bg-amber-500/20 text-amber-300',
                      )}
                      style={{
                        width: `${Math.max((d.count / maxDropout) * 100, 8)}%`,
                      }}
                    >
                      <span className="text-sm font-semibold tabular-nums">
                        {d.count}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
                </button>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Drilldown */}
        {selectedStage && (
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-sm">
                    {stageLabel[selectedStage] ?? selectedStage}
                  </CardTitle>
                  <button
                    onClick={() => setSelectedStage(null)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <StageContacts stage={selectedStage} />
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <div className="text-xs text-muted-foreground border-t border-border pt-4">
        <strong>Stages canônicos:</strong> suspect → visitor → engaged → identified → checkout_started →
        payment_pending → customer_front → upsell_decision → customer_pro → install_decision →
        active_customer · saídas: payment_failed · at_risk · lost · won.{' '}
        <strong>Dropoff</strong> = percentual de saída do stage anterior; valores negativos
        (↑) indicam ganho líquido (mais entrou que saiu naquele snapshot).
      </div>
    </div>
  );
}
